import { useState, useEffect } from 'react'
import type { ReferenceLab } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  Modal,
  FormField,
  ConfirmDialog,
  ColumnSettingsButton,
  StatusBadge,
  btnPrimary,
  btnSecondary,
  btnDanger,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface Form {
  code: string
  name: string
  npi: string
  connectionType: string
  contactName: string
  contactEmail: string
  contactPhone: string
  notes: string
  endpointConfigJson: string
  testMappingJson: string
}

const defaults: Form = {
  code: '',
  name: '',
  npi: '',
  connectionType: 'manual',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
  endpointConfigJson: '{}',
  testMappingJson: '[]',
}

const CONNECTION_TYPES: readonly { value: string; label: string }[] = [
  { value: 'manual', label: 'Manual (fax/email)' },
  { value: 'hl7', label: 'HL7' },
  { value: 'sftp', label: 'SFTP' },
  { value: 'soap', label: 'SOAP' },
  { value: 'rest', label: 'REST API' },
  { value: 's3', label: 'S3' },
]

export function ReferenceLabListPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<ReferenceLab>({
    path: '/reference-labs',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('reference-labs')
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<ReferenceLab | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [form, setForm] = useState<Form>(defaults)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!modalOpen) return
    setError(null)
    if (isNew || !selected) {
      setForm(defaults)
      return
    }
    setForm({
      code: selected.code,
      name: selected.name,
      npi: selected.npi ?? '',
      connectionType: selected.connectionType,
      contactName: selected.contactName ?? '',
      contactEmail: selected.contactEmail ?? '',
      contactPhone: selected.contactPhone ?? '',
      notes: selected.notes ?? '',
      endpointConfigJson: JSON.stringify(selected.endpointConfig, null, 2),
      testMappingJson: JSON.stringify(selected.testMapping, null, 2),
    })
  }, [modalOpen, isNew, selected])

  const isSystem = !isNew && (selected?.isSystem ?? false)

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((prev) => ({ ...prev, [k]: v }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      let endpointConfig: unknown
      let testMapping: unknown
      try {
        endpointConfig = JSON.parse(form.endpointConfigJson || '{}')
        testMapping = JSON.parse(form.testMappingJson || '[]')
        if (!Array.isArray(testMapping)) throw new Error('testMapping must be a JSON array')
      } catch (err) {
        setError(`JSON invalid: ${err instanceof Error ? err.message : String(err)}`)
        setSaving(false)
        return
      }
      const payload: Record<string, unknown> = {
        code: form.code,
        name: form.name,
        connectionType: form.connectionType,
        endpointConfig,
        testMapping,
      }
      for (const k of ['npi', 'contactName', 'contactEmail', 'contactPhone', 'notes'] as const) {
        if (form[k]) payload[k] = form[k]
      }
      if (isNew) await api.post<ReferenceLab>('/reference-labs', payload)
      else await api.put<ReferenceLab>(`/reference-labs/${selected?.id}`, payload)
      refresh()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!selected) return
    setArchiving(true)
    try {
      await api.delete(`/reference-labs/${selected.id}`)
      refresh()
      archiveModal.close()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const columns: readonly Column<ReferenceLab>[] = [
    { key: 'code', header: 'Code', sortable: true, filterable: true, render: (r) => <strong>{r.code}</strong> },
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (r) => r.name },
    { key: 'npi', header: 'NPI', render: (r) => r.npi ?? '-' },
    { key: 'conn', header: 'Connection', sortable: true, render: (r) => r.connectionType },
    { key: 'mappings', header: 'Test Mappings', render: (r) => `${r.testMapping.length}` },
    {
      key: 'isSystem',
      header: 'System',
      render: (r) => (r.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    { key: 'status', header: 'Status', sortable: true, render: (r) => (r.isActive ? 'Active' : 'Archived') },
  ]

  const title = isNew ? 'New Reference Lab' : `${form.code} - ${form.name}`

  return (
    <div>
      <PageHeader
        title="Reference Labs"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button
              type="button"
              onClick={() => {
                setSelected(null)
                setIsNew(true)
                setModalOpen(true)
              }}
              style={btnPrimary}
            >
              Add Reference Lab
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(r) => {
              setSelected(r)
              setIsNew(false)
              setModalOpen(true)
            }}
            emptyMessage="No reference labs yet. Add one to enable test send-outs."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={title} width="780px">
        <div>
          {isSystem && (
            <div
              style={{
                padding: '0.625rem 0.875rem',
                marginBottom: '1rem',
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '4px',
                fontSize: '0.875rem',
                color: '#92400e',
              }}
              data-testid="system-record-notice"
            >
              This is a system-provided reference lab and cannot be edited or archived.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '0.75rem' }}>
            <FormField
              name="code"
              label="Code"
              required
              value={form.code}
              onChange={(v) => update('code', v)}
              disabled={isSystem}
            />
            <FormField
              name="name"
              label="Name"
              required
              value={form.name}
              onChange={(v) => update('name', v)}
              disabled={isSystem}
            />
            <FormField name="connectionType" label="Connection" required>
              <select
                style={input}
                value={form.connectionType}
                onChange={(e) => update('connectionType', e.target.value)}
                disabled={isSystem}
              >
                {CONNECTION_TYPES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}
          >
            <FormField
              name="npi"
              label="NPI"
              value={form.npi}
              onChange={(v) => update('npi', v)}
              disabled={isSystem}
              placeholder="10-digit NPI"
            />
            <FormField
              name="contactName"
              label="Contact Name"
              value={form.contactName}
              onChange={(v) => update('contactName', v)}
            />
            <FormField
              name="contactEmail"
              label="Contact Email"
              type="email"
              value={form.contactEmail}
              onChange={(v) => update('contactEmail', v)}
            />
            <FormField
              name="contactPhone"
              label="Contact Phone"
              value={form.contactPhone}
              onChange={(v) => update('contactPhone', v)}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="endpointConfigJson" label="Endpoint Config (JSON)">
              <textarea
                style={{
                  ...input,
                  minHeight: '100px',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                }}
                value={form.endpointConfigJson}
                onChange={(e) => update('endpointConfigJson', e.target.value)}
                placeholder='{"host":"sftp.example.com","port":22,"username":"env:REFLAB_USER"}'
              />
            </FormField>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="testMappingJson" label="Test Mapping (JSON array)">
              <textarea
                style={{
                  ...input,
                  minHeight: '100px',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                }}
                value={form.testMappingJson}
                onChange={(e) => update('testMappingJson', e.target.value)}
                placeholder='[{"localTestTypeCode":"CBC","refLabCode":"85025"}]'
              />
            </FormField>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="notes" label="Notes">
              <textarea
                style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
              />
            </FormField>
          </div>
          {error && (
            <div
              style={{
                marginTop: '0.75rem',
                color: colors.danger,
                padding: '0.5rem 0.75rem',
                background: colors.bgError,
                border: `1px solid ${colors.borderError}`,
                borderRadius: '3px',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            {!isNew && !isSystem && (
              <button
                type="button"
                onClick={archiveModal.open}
                style={{ ...btnDanger, marginRight: 'auto' }}
                disabled={saving || archiving}
              >
                Archive
              </button>
            )}
            <button type="button" onClick={() => setModalOpen(false)} style={btnSecondary} disabled={saving}>
              {isSystem ? 'Close' : 'Cancel'}
            </button>
            {!isSystem && (
              <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Reference Lab"
        message={`Archive ${form.name}? Existing send-outs are preserved but new orders cannot route here.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
